import { Form, notification } from 'antd';
import {
    FC, createContext, useState, ReactNode,
    useEffect,
} from 'react';
import { initialContext } from './tours.initial';
import { ICreateTourPayload, IToursContext } from './tours.interface';
import { Tour, TourStep, SelectedElement } from '../../../interfaces/tours';
import {
    getTours,
    createTour as createTourAPI,
    deleteTour as deleteTourAPI,
    updateTour as updateTourAPI,
} from '../../../api/tours';
import { getWorkspaces } from '../../../api/workspaces';
import { COUNTRIES } from '../../../const/countries';
import { ETourProject, ETourType } from '../../../enums/tours.enum';

export const ToursContext = createContext<IToursContext>(initialContext);

interface ToursProviderProps {
    children: ReactNode;
}

export const ToursProvider: FC<ToursProviderProps> = ({ children }) => {
    const [currentTour, setCurrentTour] = useState<Tour | null>(initialContext.currentTour);
    const [steps, setSteps] = useState<TourStep[]>(initialContext.steps);
    const [currentPath, setCurrentPath] = useState<string>('/');

    const [selectionMode, setSelectionMode] = useState<boolean>(initialContext.selectionMode);
    const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(initialContext.selectedElement);
    const [showStepForm, setShowStepForm] = useState<boolean>(initialContext.showStepForm);
    const [showCreateTour, setShowCreateTour] = useState<boolean>(initialContext.showCreateTour);
    const [isAddingStep, setIsAddingStep] = useState<boolean>(initialContext.isAddingStep);
    const [tourConfigured, setTourConfigured] = useState<boolean>(initialContext.tourConfigured);

    const [loading, setLoading] = useState<boolean>(initialContext.loading);
    const [savingTour, setSavingTour] = useState<boolean>(initialContext.savingTour);
    const [loadingCountries] = useState<boolean>(false);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState<boolean>(initialContext.loadingWorkspaces);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    const [countries] = useState<any[]>(COUNTRIES);
    const [tours, setTours] = useState<Tour[]>(initialContext.tours);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [pageSize] = useState<number>(10);

    const [stepSelected, setStepSelected] = useState<TourStep | null>(null);
    const [tourSelected, setTourSelected] = useState<Tour | null>(null);

    const [form] = Form.useForm();

    const resetTour = () => {
        setCurrentTour(null);
        setSteps([]);
        setShowStepForm(false);
        setSelectedElement(null);
        setSelectionMode(false);
        setShowCreateTour(false);
        setIsAddingStep(false);
        setTourConfigured(false);
        setStepSelected(null);
        setTourSelected(null);
    };

    const fetchTours = () => {
        setLoading(true);
        setCurrentPage(1);
        setHasMore(true);
        getTours({
            order: 'DESC',
            page: 1,
            limit: pageSize,
        })
            .then((response: any) => {
                const { data } = response;
                const toursData = data.data || [];
                setTours(toursData);
                setHasMore(toursData.length === pageSize);
            })
            .catch((error: any) => {
                console.error('Failed to load tours:', error);
                notification.error({
                    message: 'Error',
                    description: 'Failed to load tours',
                });
            })
            .finally(() => setLoading(false));
    };

    const fetchMoreTours = async () => {
        if (loadingMore || !hasMore) {
            return;
        }
        setLoadingMore(true);
        const nextPage = currentPage + 1;
        try {
            const response: any = await getTours({
                order: 'DESC',
                page: nextPage,
                limit: pageSize,
            });
            const { data } = response;
            const newTours = data.data || [];
            if (newTours.length > 0) {
                setTours((prevTours) => [...prevTours, ...newTours]);
                setCurrentPage(nextPage);
                setHasMore(newTours.length === pageSize);
            } else {
                setHasMore(false);
            }
        } catch (error: any) {
            console.error('Failed to load more tours:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to load more tours',
            });
        } finally {
            setLoadingMore(false);
        }
    };

    const createTourAction = (tourData: Partial<Tour>) => {
        setCurrentTour({
            ...tourData,
        } as Tour);
    };

    const editStep = (stepId: string, updates: Partial<TourStep>) => {
        setSteps((prevSteps) => prevSteps.map((step) => (step.id === stepId ? { ...step, ...updates } : step)));
    };

    const deleteStep = (stepId: string) => {
        setSteps((prevSteps) => prevSteps.filter((step) => step.id !== stepId));
    };

    const reorderSteps = (reorderedSteps: TourStep[]) => {
        setSteps(reorderedSteps);
    };

    const selectElement = (selector: string) => {
        setSelectedElement({ selector });
        setShowStepForm(true);
        setSelectionMode(false);
    };

    const updateTour = async (tourId: string, payload: Partial<ICreateTourPayload>): Promise<void> => {
        if (!tourId) {
            notification.error({
                message: 'Error',
                description: 'Tour ID is required',
            });
            return;
        }
        setLoading(true);
        updateTourAPI(tourId, payload)
            .then(() => {
                notification.success({
                    message: 'Success',
                    description: 'Tour updated successfully',
                });
                setTours((prevTours) => prevTours.map((tour) => (tour._id === tourId ? { ...tour, ...payload } : tour)));
            })
            .catch((error: any) => {
                const description = error?.response?.data?.description || 'Failed to update tour';
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setLoading(false));
    };

    const saveTour = async (values: Partial<ICreateTourPayload>): Promise<void> => {
        if (!values) {
            notification.error({
                message: 'Error',
                description: 'No tour to save',
            });
            return;
        }

        if (!values.name) {
            notification.error({
                message: 'Error',
                description: 'Tour must have a name',
            });
            return;
        }

        if (values.targetIds?.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Must select targeting',
            });
            return;
        }

        setSavingTour(true);

        createTourAPI({ ...values, type: values.type || ETourType.ON_VISIT })
            .then((response: any) => {
                notification.success({
                    message: 'Success',
                    description: 'Tour saved successfully',
                });

                const createdTour: Tour | undefined = response?.data?.data;
                if (createdTour) {
                    setTours((prevTours) => [createdTour, ...prevTours]);
                    setTourSelected(createdTour);
                    setShowCreateTour(true);
                } else {
                    fetchTours();
                }

                setCurrentTour(null);
            })
            .catch((error: any) => {
                const description = error?.response?.data?.description || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setSavingTour(false));
    };

    const deleteTour = async (tourId: string): Promise<void> => {
        if (!tourId) {
            notification.error({
                message: 'Error',
                description: 'Tour ID is required',
            });
            return;
        }

        setLoading(true);

        try {
            await deleteTourAPI(tourId);

            notification.success({
                message: 'Success',
                description: 'Tour deleted successfully',
            });

            setTours((prevTours) => prevTours.filter((tour) => tour._id !== tourId));

            if (tourSelected?._id === tourId) {
                setTourSelected(null);
            }
        } catch (error: any) {
            const description = error?.response?.data?.description || 'Failed to delete tour';
            notification.error({
                message: 'Error',
                description,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchWorkspacesAsync = async (params: { page: number; pageSize: number; search?: string, workspaceIDs?: string[] }) => {
        setLoadingWorkspaces(true);
        try {
            const paginator = { limit: params.pageSize, page: params.page };
            const { data } = await getWorkspaces({ paginator, search: params.search, workspaceIDs: params.workspaceIDs });
            return {
                items: data.data,
                total: data.meta?.itemCount || 0,
            };
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to load workspaces',
            });
            return { items: [], total: 0 };
        } finally {
            setLoadingWorkspaces(false);
        }
    };

    const startAddingStep = (
        stepSelected: TourStep | null = null,
    ) => {
        setIsAddingStep(true);
        setSelectionMode(true);
        setSelectedElement(null);
        setShowStepForm(false);

        if (stepSelected) {
            setStepSelected(stepSelected);
        }
    };

    const cancelAddingStep = () => {
        setIsAddingStep(false);
        setSelectionMode(false);
        setSelectedElement(null);
        setShowStepForm(false);
    };

    const completeTourConfiguration = () => {
        setTourConfigured(true);
        setShowCreateTour(false);
    };

    const changeElementSelection = () => {
        setSelectionMode(true);
        setSelectedElement(null);
        setShowStepForm(false);
    };

    const addStepFlow = (step: Omit<TourStep, 'id'>) => {
        const newStep: TourStep = {
            ...step,
            id: Date.now().toString(),
        };
        setSteps((prevSteps) => [...prevSteps, newStep]);

        setIsAddingStep(false);
        setShowStepForm(false);
        setSelectedElement(null);
        setSelectionMode(false);
        setStepSelected(null);
    };

    const cancelAddingTour = () => {
        setShowCreateTour(false);
        setCurrentTour(null);
        setSteps([]);
        setShowStepForm(false);
        setSelectedElement(null);
        setSelectionMode(false);
        setIsAddingStep(false);
        setTourConfigured(false);
        setStepSelected(null);
        setTourSelected(null);
    };

    const startEditingTour = (tour: Tour) => {
        setTourSelected(tour);
        setShowCreateTour(true);
    };

    useEffect(() => {
        fetchTours();
    }, []);

    return (
        <ToursContext.Provider
            value={{
                currentTour,
                steps,
                currentPath,

                selectionMode,
                selectedElement,
                showStepForm,
                showCreateTour,
                isAddingStep,
                tourConfigured,

                loading,
                savingTour,
                loadingCountries,
                loadingWorkspaces,

                countries,
                tours,
                form,

                stepSelected,
                tourSelected,
                setTourSelected,
                createTour: createTourAction,
                addStep: addStepFlow,
                editStep,
                deleteStep,
                reorderSteps,
                selectElement,
                saveTour,
                deleteTour,
                fetchTours,
                setCurrentTour,
                setCurrentPath,
                setShowCreateTour,
                setShowStepForm,
                fetchWorkspacesAsync,
                resetTour,
                startAddingStep,
                cancelAddingStep,
                completeTourConfiguration,
                changeElementSelection,
                cancelAddingTour,
                setStepSelected,
                updateTour,
                startEditingTour,
                fetchMoreTours,
                loadingMore,
                hasMore,
            }}
        >
            {children}
        </ToursContext.Provider>
    );
};
